
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Package, DollarSign, TrendingUp, Clock } from "lucide-react";

const OrderStats = () => {
  const { data: stats, isLoading } = useQuery({
    queryKey: ['order-stats'],
    queryFn: async () => {
      const { data: orders, error } = await supabase
        .from('orders')
        .select('total_amount, status, created_at');

      if (error) throw error;

      const totalOrders = orders.length;
      const totalRevenue = orders.reduce((sum, order) => sum + Number(order.total_amount), 0);
      const pendingOrders = orders.filter(order => order.status === 'pending').length;
      
      // Calculate average order value
      const avgOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;

      return {
        totalOrders,
        totalRevenue,
        pendingOrders,
        avgOrderValue
      };
    },
  });

  const statCards = [
    {
      title: "Total Orders",
      value: stats?.totalOrders || 0,
      icon: Package,
      formatter: (value: number) => value.toString()
    },
    {
      title: "Total Revenue",
      value: stats?.totalRevenue || 0,
      icon: DollarSign,
      formatter: (value: number) => `$${value.toFixed(2)}`
    },
    {
      title: "Pending Orders",
      value: stats?.pendingOrders || 0,
      icon: Clock,
      formatter: (value: number) => value.toString()
    },
    {
      title: "Avg Order Value",
      value: stats?.avgOrderValue || 0,
      icon: TrendingUp,
      formatter: (value: number) => `$${value.toFixed(2)}`
    }
  ];

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        {[...Array(4)].map((_, i) => (
          <Card key={i} className="animate-pulse">
            <CardContent className="p-6">
              <div className="h-8 bg-gray-200 rounded mb-2"></div>
              <div className="h-6 bg-gray-200 rounded"></div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
      {statCards.map((stat) => {
        const Icon = stat.icon;
        return (
          <Card key={stat.title} className="admin-card border-admin-border">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-admin-muted-foreground">
                {stat.title}
              </CardTitle>
              <Icon className="h-4 w-4 text-admin-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-admin-foreground">
                {stat.formatter(stat.value)}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

export default OrderStats;
